import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TopBar from '../../components/TopBar';
import Splash from '../../components/Splash';
import {
  adminPaymentCards,
  adminCreatePaymentCard,
  adminUpdatePaymentCard,
  adminDeletePaymentCard,
} from '../../api/endpoints';
import { showAlert, showConfirm, hapticNotify } from '../../lib/tg';
import type { PaymentCard } from '../../types/models';

function maskCard(n: string): string {
  const digits = n.replace(/\D/g, '');
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

export default function PaymentCards() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-payment-cards'],
    queryFn: adminPaymentCards,
  });

  const [number, setNumber] = useState('');
  const [holder, setHolder] = useState('');

  const addMut = useMutation({
    mutationFn: () =>
      adminCreatePaymentCard({
        number: number.replace(/\s+/g, ''),
        holder: holder.trim() || null,
        is_active: true,
      } as Partial<PaymentCard>),
    onSuccess: () => {
      hapticNotify('success');
      setNumber('');
      setHolder('');
      qc.invalidateQueries({ queryKey: ['admin-payment-cards'] });
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      showAlert(typeof detail === 'string' ? detail : 'Не удалось добавить карту');
    },
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      adminUpdatePaymentCard(id, { is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-payment-cards'] }),
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      showAlert(typeof detail === 'string' ? detail : 'Не удалось обновить карту');
    },
  });

  const delMut = useMutation({
    mutationFn: (id: number) => adminDeletePaymentCard(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-payment-cards'] }),
  });

  async function onDelete(id: number) {
    if (await showConfirm('Удалить карту?')) delMut.mutate(id);
  }

  if (isLoading) return <Splash />;

  const numberClean = number.replace(/\s+/g, '');
  const canAdd = numberClean.length >= 12 && !addMut.isPending;

  return (
    <div className="min-h-screen pb-8">
      <TopBar title="Карты для оплаты" />
      <div className="px-4 pt-3 space-y-3">
        {data?.map((c) => (
          <div key={c.id} className="card p-3 flex items-center gap-3">
            <button
              onClick={() => toggleMut.mutate({ id: c.id, is_active: !c.is_active })}
              disabled={toggleMut.isPending}
              className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                c.is_active ? 'border-accent bg-accent' : 'border-line bg-transparent'
              }`}
              aria-label={c.is_active ? 'Скрыть карту' : 'Показывать карту'}
            >
              {c.is_active && <div className="w-2 h-2 rounded-full bg-white" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-mono tracking-wider text-sm truncate">
                {maskCard(c.number)}
              </div>
              {c.holder && <div className="text-xs text-muted mt-0.5 truncate">{c.holder}</div>}
              <div className="text-xs text-muted mt-0.5">
                {c.is_active ? 'Показывается клиентам' : 'Скрыта'}
              </div>
            </div>
            <button className="text-danger text-sm shrink-0" onClick={() => onDelete(c.id)}>
              Удалить
            </button>
          </div>
        ))}
        {!data?.length && <div className="text-muted text-center py-6">Нет карт</div>}

        <div className="card p-3 space-y-3">
          <div className="font-semibold">Новая карта</div>
          <div>
            <div className="label">Номер карты</div>
            <input
              className="input font-mono tracking-wider"
              placeholder="0000 0000 0000 0000"
              inputMode="numeric"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
            />
          </div>
          <div>
            <div className="label">Держатель (необязательно)</div>
            <input
              className="input"
              placeholder="IVAN IVANOV"
              value={holder}
              onChange={(e) => setHolder(e.target.value)}
            />
          </div>
          <button className="btn-primary" disabled={!canAdd} onClick={() => addMut.mutate()}>
            Добавить
          </button>
        </div>
      </div>
    </div>
  );
}
